/**
 * Trail Configuration Form Component
 * 
 * Form for creating/editing trail configurations
 * - Trail name input (required, max 100 chars)
 * - Direction dropdown (required, predefined options)
 * - Is default checkbox (optional)
 * - Form validation
 * 
 * Requirements: 6.1, 6.2, 6.3, 7.2
 */

import { useState } from 'react';
import { TrailConfiguration } from '../../../api/rest-client';
import { Button } from '../../common/Button';

interface TrailConfigurationFormProps {
  trailConfiguration?: TrailConfiguration;
  onSubmit: (data: Omit<TrailConfiguration, 'trail_id' | 'is_active' | 'created_at' | 'updated_at'>) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

const DIRECTION_OPTIONS = [
  'Clockwise',
  'Counter-clockwise',
  'Out and Back',
  'Point to Point',
  'Other'
] as const;

export function TrailConfigurationForm({ 
  trailConfiguration, 
  onSubmit, 
  onCancel, 
  isLoading 
}: TrailConfigurationFormProps) {
  const [formData, setFormData] = useState({
    trail_name: trailConfiguration?.trail_name || '',
    direction: trailConfiguration?.direction || '',
    is_default: trailConfiguration?.is_default || false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.trail_name.trim()) {
      newErrors.trail_name = 'Trail name is required';
    } else if (formData.trail_name.length > 100) {
      newErrors.trail_name = 'Trail name must be 100 characters or less';
    }
    
    if (!formData.direction) {
      newErrors.direction = 'Direction is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Trail Name */}
      <div>
        <label htmlFor="trail_name" className="block text-sm font-medium text-gray-700 mb-1">
          Trail Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="trail_name"
          name="trail_name"
          value={formData.trail_name}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.trail_name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="e.g., Dwelling"
          maxLength={100}
          disabled={isLoading}
        />
        {errors.trail_name && <p className="mt-1 text-sm text-red-600">{errors.trail_name}</p>}
        <p className="mt-1 text-xs text-gray-500">
          {formData.trail_name.length}/100 characters
        </p>
      </div>

      {/* Direction */}
      <div>
        <label htmlFor="direction" className="block text-sm font-medium text-gray-700 mb-1">
          Direction <span className="text-red-500">*</span>
        </label>
        <select
          id="direction"
          name="direction"
          value={formData.direction}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.direction ? 'border-red-500' : 'border-gray-300'
          }`}
          disabled={isLoading}
        >
          <option value="">Select Direction...</option>
          {DIRECTION_OPTIONS.map((dir) => (
            <option key={dir} value={dir}>
              {dir}
            </option>
          ))}
        </select>
        {errors.direction && <p className="mt-1 text-sm text-red-600">{errors.direction}</p>}
      </div>

      {/* Is Default */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="is_default"
          name="is_default"
          checked={formData.is_default}
          onChange={handleChange}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          disabled={isLoading}
        />
        <label htmlFor="is_default" className="ml-2 block text-sm text-gray-700">
          Set as default trail
        </label>
      </div>
      <p className="text-xs text-gray-500 ml-6">
        The default trail will be pre-selected when recording time trials
      </p>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4">
        <Button 
          type="submit" 
          variant="primary" 
          disabled={isLoading} 
          className="flex-1"
        >
          {isLoading ? 'Saving...' : trailConfiguration ? 'Update' : 'Create'}
        </Button>
        <Button 
          type="button" 
          variant="secondary" 
          onClick={onCancel} 
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
